import { useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { RotateCw } from "lucide-react";
import { GROUP_SELECTION_ID } from "./canvasGroupTransform";
import { ROTATE_HANDLE_CORNER, cssRotateTransform } from "./layerRotation";
import { useBoardOverlayFrame } from "./useBoardOverlayFrame";
import { boardFrameClipPath } from "./boardFrameClipPath";

export const CANVAS_SELECTION_HANDLE_CLASS = "canvas-selection-resize-handle";

const HANDLE_SIZE = 10;
const LABEL_HEIGHT = 18;
const ROTATE_HANDLE_SIZE = 18;
const ROTATE_HANDLE_GAP = 18;
const ROTATE_STEM_HEIGHT = ROTATE_HANDLE_GAP - 2;
const SHOW_ROTATE_HANDLE = true;

const RESIZE_HANDLES = [
  { corner: "n", cursor: "ns-resize" },
  { corner: "s", cursor: "ns-resize" },
  { corner: "e", cursor: "ew-resize" },
  { corner: "w", cursor: "ew-resize" },
  { corner: "nw", cursor: "nwse-resize" },
  { corner: "ne", cursor: "nesw-resize" },
  { corner: "sw", cursor: "nesw-resize" },
  { corner: "se", cursor: "nwse-resize" },
];

const getResizeHandles = (selection) => {
  if (
    !Array.isArray(selection.handleCorners) ||
    selection.handleCorners.length === 0
  ) {
    return RESIZE_HANDLES;
  }

  const allowed = new Set(selection.handleCorners);
  return RESIZE_HANDLES.filter((handle) => allowed.has(handle.corner));
};

const CROP_TICK = 7;
const CROP_STROKE = 1.5;

const handlePositionSx = ({ corner }) => {
  const offsetPx = HANDLE_SIZE / 2;
  if (corner === "n" || corner === "s") {
    return {
      left: `calc(50% - ${offsetPx}px)`,
      ...(corner === "n" ? { top: -offsetPx } : { bottom: -offsetPx }),
    };
  }
  if (corner === "e" || corner === "w") {
    return {
      top: `calc(50% - ${offsetPx}px)`,
      ...(corner === "w" ? { left: -offsetPx } : { right: -offsetPx }),
    };
  }
  const sx = {};
  if (corner.includes("n")) {
    sx.top = -offsetPx;
  } else {
    sx.bottom = -offsetPx;
  }
  if (corner.includes("w")) {
    sx.left = -offsetPx;
  } else {
    sx.right = -offsetPx;
  }
  return sx;
};

const cropTickSx = ({ color }) => ({
  position: "absolute",
  bgcolor: color,
  boxShadow: "0 0 0 1px #fff",
  pointerEvents: "none",
});

const CropHandleMarks = ({ corner, color }) => {
  const tick = cropTickSx({ color });
  if (corner === "n" || corner === "s") {
    return (
      <>
        <Box
          sx={{
            ...tick,
            left: 1,
            width: HANDLE_SIZE - 2,
            height: CROP_STROKE,
            top: 2,
          }}
        />
        <Box
          sx={{
            ...tick,
            left: 1,
            width: HANDLE_SIZE - 2,
            height: CROP_STROKE,
            bottom: 2,
          }}
        />
      </>
    );
  }
  if (corner === "e" || corner === "w") {
    return (
      <>
        <Box
          sx={{
            ...tick,
            top: 1,
            height: HANDLE_SIZE - 2,
            width: CROP_STROKE,
            left: 2,
          }}
        />
        <Box
          sx={{
            ...tick,
            top: 1,
            height: HANDLE_SIZE - 2,
            width: CROP_STROKE,
            right: 2,
          }}
        />
      </>
    );
  }
  const north = corner.includes("n");
  const west = corner.includes("w");
  return (
    <>
      <Box
        sx={{
          ...tick,
          width: CROP_TICK,
          height: CROP_STROKE,
          ...(north ? { top: 1 } : { bottom: 1 }),
          ...(west ? { left: 1 } : { right: 1 }),
        }}
      />
      <Box
        sx={{
          ...tick,
          width: CROP_STROKE,
          height: CROP_TICK,
          ...(north ? { top: 1 } : { bottom: 1 }),
          ...(west ? { left: 1 } : { right: 1 }),
        }}
      />
    </>
  );
};

const SelectionChromeOverlay = ({
  boardRef,
  viewportRef,
  selections = [],
  syncKey,
  clipToCanvas = false,
  onHandlePointerDown,
}) => {
  const overlayRef = useRef(null);
  const frame = useBoardOverlayFrame({
    boardRef,
    overlayRef,
    viewportRef,
    syncKey,
  });
  const clipPath = clipToCanvas ? boardFrameClipPath(frame) : undefined;

  return (
    <Box
      ref={overlayRef}
      data-template-chrome-root=""
      sx={{
        position: "absolute",
        inset: 0,
        overflow: clipToCanvas ? "hidden" : "visible",
        clipPath,
        pointerEvents: "none",
        zIndex: 4,
      }}
    >
      {selections.map((selection) => (
        <Box
          key={selection.id}
          data-template-chrome-id={selection.id}
          sx={{
            position: "absolute",
            left: frame.left + selection.x,
            top: frame.top + selection.y,
            width: Math.max(0, selection.width),
            height: Math.max(0, selection.height),
            boxSizing: "border-box",
            pointerEvents: "none",
          }}
        >
          {selection.showLabel ? (
            <Typography
              sx={{
                position: "absolute",
                top: `-${LABEL_HEIGHT}px`,
                left: 0,
                fontSize: "10.5px",
                lineHeight: `${LABEL_HEIGHT}px`,
                px: 0.75,
                borderRadius: "5px",
                bgcolor: selection.color,
                color: "#fff",
                whiteSpace: "nowrap",
                pointerEvents: "none",
                zIndex: 3,
              }}
            >
              {selection.name}
            </Typography>
          ) : null}
          <Box
            data-template-chrome-rotate=""
            sx={{
              position: "absolute",
              inset: 0,
              border: `2px solid ${selection.color}`,
              boxShadow: "0 0 0 3px rgba(24,24,27,.2)",
              boxSizing: "border-box",
              pointerEvents: "none",
              transform: cssRotateTransform(selection.rotation),
              transformOrigin: "center center",
            }}
          >
            {selection.showHandles
              ? getResizeHandles(selection).map((handle) => (
                  <Box
                    key={handle.corner}
                    className={CANVAS_SELECTION_HANDLE_CLASS}
                    onPointerDown={(event) =>
                      onHandlePointerDown?.({
                        event,
                        id: selection.id,
                        corner: handle.corner,
                      })
                    }
                    sx={{
                      position: "absolute",
                      ...handlePositionSx({ corner: handle.corner }),
                      width: HANDLE_SIZE,
                      height: HANDLE_SIZE,
                      borderRadius: selection.cropHandles ? 0 : "2px",
                      bgcolor: selection.cropHandles
                        ? "transparent"
                        : selection.color,
                      border: selection.cropHandles ? "none" : "1px solid #fff",
                      cursor: handle.cursor,
                      pointerEvents: "auto",
                      zIndex: 2,
                      boxSizing: "border-box",
                    }}
                  >
                    {selection.cropHandles ? (
                      <CropHandleMarks
                        corner={handle.corner}
                        color={selection.color}
                      />
                    ) : null}
                  </Box>
                ))
              : null}
            {selection.showHandles &&
            SHOW_ROTATE_HANDLE &&
            !selection.cropHandles &&
            selection.id !== GROUP_SELECTION_ID ? (
              <>
                <Box
                  sx={{
                    position: "absolute",
                    top: -ROTATE_STEM_HEIGHT,
                    left: "50%",
                    width: 0,
                    height: ROTATE_STEM_HEIGHT,
                    borderLeft: `1.5px solid ${selection.color}`,
                    transform: "translateX(-50%)",
                    pointerEvents: "none",
                  }}
                />
                <Box
                  className={CANVAS_SELECTION_HANDLE_CLASS}
                  onPointerDown={(event) =>
                    onHandlePointerDown?.({
                      event,
                      id: selection.id,
                      corner: ROTATE_HANDLE_CORNER,
                    })
                  }
                  sx={{
                    position: "absolute",
                    top: -(ROTATE_HANDLE_GAP + ROTATE_HANDLE_SIZE / 2),
                    left: "50%",
                    width: ROTATE_HANDLE_SIZE,
                    height: ROTATE_HANDLE_SIZE,
                    ml: `-${ROTATE_HANDLE_SIZE / 2}px`,
                    borderRadius: "50%",
                    bgcolor: "#fff",
                    border: `1.5px solid ${selection.color}`,
                    color: selection.color,
                    cursor: "grab",
                    pointerEvents: "auto",
                    zIndex: 3,
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:active": { cursor: "grabbing" },
                  }}
                >
                  <RotateCw size={11} strokeWidth={2.4} />
                </Box>
              </>
            ) : null}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default SelectionChromeOverlay;
